import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, Phone, Truck } from "lucide-react";
import { Stamp } from "./Stamp";
import { PHONE, DELIVERY, mailto } from "./data";

const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "105%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.25]);
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 80, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden bg-[#1A110C] text-[#FDFBF7] grain"
    >
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src="/img/hero.jpg" alt="Fuoco caldo nel camino" className="w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A110C]/70 via-[#1A110C]/40 to-[#1A110C]" />
      </motion.div>

      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 pt-28 sm:pt-36 pb-16 grid lg:grid-cols-12 gap-10 items-end min-h-[100svh]">
        <motion.div style={{ y: titleY }} className="lg:col-span-8 relative">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#E8893F]"
          >
            <span className="h-px w-10 bg-[#E8893F]" /> Pellet in vero legno di abete
          </motion.p>
          <h1 data-testid="hero-title" className="font-serif font-medium leading-[0.86] tracking-[-0.03em] text-[19vw] sm:text-[15vw] lg:text-[10.5rem] xl:text-[12rem]">
            <Line delay={0.35}>Pellet</Line>
            <Line delay={0.5} className="italic text-[#E8893F] pl-[8vw] lg:pl-32">Italiana</Line>
          </h1>

          <motion.div
            initial={{ opacity: 0, rotate: -14, scale: 1.6 }}
            animate={{ opacity: 1, rotate: -7, scale: 1 }}
            transition={{ duration: 0.5, delay: 1.35, ease: [0.5, 0, 0.2, 1.4] }}
            data-testid="hero-collab-overprint"
            className="overprint inline-block mt-6 sm:mt-2 sm:absolute sm:right-4 sm:top-28 lg:right-0 lg:top-32 border-2 border-[#E8893F] text-[#E8893F] px-4 py-2.5 rounded-md uppercase tracking-[0.18em] text-[11px] sm:text-xs font-bold"
          >
            In collaborazione con <span className="text-[#FDFBF7]">Mak Holz</span>
            <span className="block text-[10px] sm:text-[11px] tracking-[0.3em] opacity-90 mt-0.5">Hobelware und Pellet</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease }}
            className="mt-10 sm:mt-16 max-w-xl"
          >
            <p className="text-base sm:text-lg text-[#F4EFE6]/85 leading-relaxed">
              Calore naturale dal vero legno di abete. Sacchi per la casa, bancali per l'inverno e forniture per negozi e rivenditori.
            </p>
            <p data-testid="hero-delivery-badge" className="mt-6 inline-flex items-center gap-2.5 rounded-full border border-[#E8893F]/50 bg-[#E8893F]/10 px-4 py-2 text-sm font-semibold text-[#F4A261]">
              <Truck className="w-4 h-4" /> {DELIVERY}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#prodotti" data-testid="hero-products-button" className="group inline-flex items-center gap-2 rounded-full bg-[#FDFBF7] text-[#1A110C] px-6 py-3.5 text-sm font-semibold hover:bg-[#E8893F] transition-colors">
                Scopri i prodotti <ArrowDownRight className="w-4 h-4 transition-transform group-hover:rotate-[-45deg]" />
              </a>
              <a href={mailto("Richiesta preventivo pellet")} data-testid="hero-quote-button" className="inline-flex items-center gap-2 rounded-full border border-[#F4EFE6]/40 px-6 py-3.5 text-sm font-semibold hover:border-[#E8893F] hover:text-[#E8893F] transition-colors">
                Richiedi preventivo
              </a>
              <a href={`tel:${PHONE}`} data-testid="hero-call-button" className="sm:hidden inline-flex items-center gap-2 rounded-full bg-[#E8893F] text-[#1A110C] px-6 py-3.5 text-sm font-semibold">
                <Phone className="w-4 h-4" /> Chiama ora
              </a>
            </div>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-4 relative [perspective:1200px] hidden sm:block">
          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            initial={{ clipPath: "inset(100% 0 0 0 round 200px 200px 24px 24px)" }}
            animate={{ clipPath: "inset(0% 0 0 0 round 200px 200px 24px 24px)" }}
            transition={{ duration: 1.4, delay: 0.6, ease }}
            className="relative aspect-[3/4] w-full max-w-[380px] ml-auto overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,0.5)]"
          >
            <img src="/img/pellets3.jpg" alt="Pellet in vero legno di abete" className="w-full h-full object-cover scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A110C]/70 to-transparent" />
            <p className="absolute bottom-5 left-5 right-5 text-xs uppercase tracking-[0.25em] text-[#F4EFE6]">100% vero legno di abete</p>
          </motion.div>
          <Stamp className="absolute -left-10 -bottom-10 w-36 h-36 lg:w-40 lg:h-40" />
        </div>

        <div className="sm:hidden relative">
          <div className="relative h-[52vh] overflow-hidden rounded-t-[160px] rounded-b-3xl">
            <img src="/img/pellets3.jpg" alt="Pellet in vero legno di abete" className="w-full h-full object-cover" />
          </div>
          <Stamp className="absolute -top-10 right-2 w-28 h-28" />
        </div>
      </div>
    </section>
  );
};
